// Detailed per-surface mocks, colored by the live tokens and highlight-aware
// (see mockPrimitives makeKit). Each renders a recognizable version of the real
// BDR mobile screen. `renderMock(pageId, mk)` returns the JSX; unknown pageIds
// fall back to a generic screen so nothing is unreachable.
//
// The layouts here are grounded in the actual Flutter page source (app bar
// treatment, ordered body sections, real field/section labels, chips, nav tabs)
// so the preview visibly matches the mobile app rather than approximating it.
import { Fragment } from "react";
import {
  AppBar, BottomNav, Fab, Card, Btn, Input, Chip, Toggle, Radio, PhotoTile,
  ListRow, Tabs, Pills, SectionLabel, Text, grad, bg,
  FLAT_CARD_BG, dividerCss,
  MockBarChart, MockLineChart, MockDonut, MockStatTile, MockRankedBars,
} from "./mockPrimitives";

// Auto-key children so composed primitive-arrays don't warn (many primitives
// are plain function calls without their own key).
const keyed = (children) =>
  (Array.isArray(children) ? children : [children]).map((ch, i) => <Fragment key={i}>{ch}</Fragment>);

const Screen = (mk, children, { nav, overlay } = {}) => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", background: bg(mk), position: "relative" }}>
    {keyed(children)}
    {nav && nav}
    {overlay && overlay}
  </div>
);
const Body = (mk, children, { gap = 8, key = "body" } = {}) => (
  <div key={key} style={{ flex: 1, overflow: "hidden", padding: 10, display: "flex", flexDirection: "column", gap }}>{keyed(children)}</div>
);
const Row = (children, { gap = 6, wrap, key } = {}) => (
  <div key={key} style={{ display: "flex", gap, flexWrap: wrap ? "wrap" : "nowrap", alignItems: "center" }}>{keyed(children)}</div>
);
const spacer = (k = "sp") => <span key={k} style={{ flex: 1 }} />;

// ── small shared helpers ────────────────────────────────────────────────────────
const dot = (mk, tok, size = 8) => (
  <span key={`d${tok}${size}`} style={{ width: size, height: size, borderRadius: "50%", background: mk.c(tok), flexShrink: 0, ...mk.glow(tok) }} />
);
// A colored 4px section bar + bold "Title (n)" header — the paycheck / payroll motif.
const sectionBar = (mk, tok, title, key) => (
  <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, margin: "2px 0" }}>
    <span style={{ width: 4, height: 13, borderRadius: 2, background: mk.c(tok), ...mk.glow(tok) }} />
    <span style={{ color: mk.c("textPrimary"), fontSize: 11, fontWeight: 700, ...mk.glow("textPrimary") }}>{title}</span>
  </div>
);
// Joined 2-segment pill toggle (Simple|Detailed, 1-Day|3-Day, Buy List|All, …).
const segToggle = (mk, labels, active = 0, key) => (
  <div key={key} style={{ display: "inline-flex", borderRadius: 9, overflow: "hidden", border: `1px solid ${mk.c("accentPrimary")}`, ...mk.glow("accentPrimary") }}>
    {labels.map((l, i) => (
      <span key={l} style={{ fontSize: 9, padding: "3px 8px", color: i === active ? mk.c("textOnPrimary") : mk.c("accentPrimary"), background: i === active ? mk.c("accentPrimary") : "transparent" }}>{l}</span>
    ))}
  </div>
);
const checkbox = (mk, on = true) => (
  <span key="cb" style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${mk.c(on ? "checkboxActive" : "checkboxOff")}`, background: on ? mk.c("checkboxActive") : "transparent", color: mk.c("checkboxCheck"), fontSize: 10, lineHeight: "12px", textAlign: "center", flexShrink: 0, ...mk.glow(on ? "checkboxActive" : "checkboxOff") }}>{on ? "✓" : ""}</span>
);

// ── Nav drawer (global chrome, NOT a page) ──────────────────────────────────────
// The drawer has no surface of its own on purpose: it is chrome that must look
// identical on every page, so it deliberately has no PageId and cannot be
// per-page overridden (AppDrawer mounts an empty ThemeScope to enforce that).
// It previews here, drawn OPEN over the two dashboard shells, because that is
// where a reviewer would look for it and how it is actually seen.
//
// Before 2026-07-30 nothing in the console rendered the drawer at all, and the
// only consumer of "Drawer Bg" was the LOGIN card — so editing it appeared to
// recolour a login screen. That is why a white-on-white drawer shipped unseen.

// Alpha-derive a token the same way dividerCss does (mk.c may return rgba()).
const atAlpha = (mk, token, a) => {
  const c = mk.c(token);
  const m = /^#([0-9a-fA-F]{6})$/.exec(c);
  if (!m) return c;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const drawerRow = (mk, icon, label, key, { pressed } = {}) => (
  <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4.5px 10px", background: pressed ? mk.c("drawerRipple") : "transparent", ...(pressed ? mk.glow("drawerRipple") : null) }}>
    <span style={{ width: 11, fontSize: 9, textAlign: "center", color: mk.c("drawerIcon"), ...mk.glow("drawerIcon") }}>{icon}</span>
    <span style={{ fontSize: 9.5, color: mk.c("drawerText"), ...mk.glow("drawerText") }}>{label}</span>
  </div>
);

const drawerSection = (mk, icon, label, expanded, key) => {
  const chevTok = expanded ? "drawerChevron" : "drawerChevronCollapsed";
  return (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4.5px 10px" }}>
      <span style={{ width: 11, fontSize: 9, textAlign: "center", color: mk.c("drawerSectionLabel"), ...mk.glow("drawerSectionLabel") }}>{icon}</span>
      <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: mk.c("drawerSectionLabel"), ...mk.glow("drawerSectionLabel") }}>{label}</span>
      <span style={{ fontSize: 8, color: mk.c(chevTok), ...mk.glow(chevTok) }}>{expanded ? "▲" : "▼"}</span>
    </div>
  );
};

const navDrawer = (mk) => (
  <div key="drawer" style={{ position: "absolute", inset: 0, display: "flex", zIndex: 5 }}>
    <div style={{ width: "66%", height: "100%", display: "flex", flexDirection: "column", background: mk.c("drawerBg"), ...mk.glow("drawerBg") }}>
      <div style={{ height: 44, flexShrink: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", background: grad(mk, "gradBrandStart", "gradBrandEnd"), borderBottom: `1px solid ${mk.c("drawerDivider")}`, ...mk.glow("gradBrandStart", "gradBrandEnd") }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: mk.c("drawerHeaderText"), ...mk.glow("drawerHeaderText") }}>Welcome Taylor</span>
        <span style={{ position: "absolute", top: 2, right: 4, fontSize: 6, color: mk.c("drawerHeaderText"), opacity: 0.5 }}>v1.2.3+274</span>
      </div>
      <div style={{ flex: 1, overflow: "hidden", paddingTop: 3 }}>
        {drawerRow(mk, "$", "My Paycheck", "r1")}
        {drawerRow(mk, "+", "Client Referral", "r2", { pressed: true })}
        {drawerRow(mk, "=", "Settings", "r3")}
        <div style={{ height: 1, margin: "3px 0", background: mk.c("drawerDivider"), ...mk.glow("drawerDivider") }} />
        {drawerSection(mk, "◆", "Admin", true, "s1")}
        {drawerRow(mk, "▪", "Shop Inventory", "r4")}
        {drawerRow(mk, "▪", "Reports", "r5")}
        {drawerSection(mk, "◇", "Developer", false, "s2")}
      </div>
      <div style={{ padding: "0 6px 5px", flexShrink: 0 }}>
        <div style={{ padding: "0 4px 2px", fontSize: 7.5, fontWeight: 700, color: mk.c("drawerSectionLabel"), ...mk.glow("drawerSectionLabel") }}>Notes</div>
        <div style={{ borderRadius: 6, padding: 5, background: mk.c("drawerNotesBg"), border: `1px solid ${atAlpha(mk, "drawerSectionLabel", 0.2)}`, ...mk.glow("drawerNotesBg") }}>
          <span style={{ fontSize: 8, color: mk.c("drawerNotesText"), ...mk.glow("drawerNotesText") }}>Submit job forms by 5pm.</span>
        </div>
      </div>
      <div style={{ height: 1, background: mk.c("drawerDivider"), ...mk.glow("drawerDivider") }} />
      {drawerRow(mk, "⏻", "Logout", "rL")}
    </div>
    <div style={{ flex: 1, height: "100%", background: mk.c("drawerScrim"), ...mk.glow("drawerScrim") }} />
  </div>
);

// ── generic templates (used by the long-tail; still faithful in shape) ──────────
const listScreen = (mk, title, rows, { actions, fab, nav } = {}) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab", actions }),
    Body(mk, rows.map((r, i) => ListRow(mk, r.t, r.s, r.chip, { key: i })), { gap: 6 }),
    fab ? Fab(mk, { key: "fab" }) : null,
  ], { nav });

const formScreen = (mk, title, fields, { toggles, radios, btn, btnKey } = {}) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      Card(mk, <>{fields.map((f, i) => <div key={i} style={{ marginBottom: i < fields.length - 1 ? 9 : 0 }}>{Input(mk, f.label, { value: f.value, focused: i === 0 })}</div>)}</>, { key: "form" }),
      toggles ? Card(mk, <>{toggles.map((t, i) => <div key={i} style={{ marginBottom: i < toggles.length - 1 ? 9 : 0 }}>{Toggle(mk, t.label, t.on !== false)}</div>)}</>, { key: "tog" }) : null,
      radios ? Card(mk, <>{radios.map((r, i) => <div key={i} style={{ marginBottom: i < radios.length - 1 ? 8 : 0 }}>{Radio(mk, r.label, i === 0)}</div>)}</>, { key: "rad" }) : null,
      Btn(mk, btn || "Submit", { a: btnKey ? btnKey[0] : "gradBlueGreenStart", b: btnKey ? btnKey[1] : "gradBlueGreenEnd", key: "btn" }),
    ]),
  ]);

// ── bespoke surfaces ────────────────────────────────────────────────────────────

// Refinisher home: week strip → alert banner → "Today's Work" + Today/3-Day →
// job accordion card. (Real page renders no app bar of its own; the shell owns it.)
const refinisherHome = (mk, title, nav, withDrawer) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      // week calendar strip
      <div key="wk" style={{ display: "flex", gap: 3 }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center", padding: "3px 0", borderRadius: 6, background: i === 3 ? mk.c("accentPrimary") : mk.c("surfaceListItem"), ...(i === 3 ? mk.glow("accentPrimary") : null) }}>
            <div style={{ fontSize: 6.5, color: i === 3 ? mk.c("textOnPrimary") : mk.c("textMuted") }}>{d}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: i === 3 ? mk.c("textOnPrimary") : mk.c("textPrimary") }}>{22 + i}</div>
          </div>
        ))}
      </div>,
      // stuck-submission banner (admin/office)
      <div key="bn" style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 6, background: mk.c("offlineBanner"), ...mk.glow("offlineBanner") }}>
        <span style={{ fontSize: 10 }}>⚑</span>
        <span style={{ flex: 1, fontSize: 8.5, color: mk.c("textOnLight") }}>2 submissions need office review</span>
      </div>,
      Row([spacer("l"), Text(mk, "Today's Work", { weight: 700, size: 12, key: "t" }), spacer("r"), segToggle(mk, ["Today", "3-Day"], 0, "tg")], { key: "hdr" }),
      Card(mk, <>
        {Row([Text(mk, "Smith — Refinish", { size: 11, weight: 700 }), spacer("s"), <span key="ic" style={{ display: "flex", gap: 4 }}>{dot(mk, "textMuted", 6)}{dot(mk, "accentPrimary", 6)}</span>])}
        {Text(mk, "Invoice #1042", { tok: "textMuted", size: 9, key: "inv" })}
        <div style={{ height: 5 }} />
        {Text(mk, "1423 Oak St", { tok: "textPrimary", size: 9, key: "ad" })}
        {Text(mk, "Weather: Clear · Gate 4821", { tok: "textMuted", size: 8, key: "wx" })}
        <div style={{ marginTop: 5, display: "flex", gap: 4 }}>{[0, 1, 2].map((j) => <div key={j} style={{ width: 30, height: 30, borderRadius: 5, background: grad(mk, "gradBrandStart", "gradBrandEnd") }} />)}</div>
      </>, { key: "job", gradKey: ["gradBrandStart", "gradBrandEnd"] }),
    ]),
  ], { nav: nav ? BottomNav(mk, nav, nav.length - 1) : null, overlay: withDrawer ? navDrawer(mk) : null });

// Owner/admin dashboard: 4 purple→blue cards — Crew, Money (In/Out/Net/Potential),
// Volume, Action Queue.
const ownerDashboard = (mk, title, nav, withDrawer) => {
  const statTile = (label, val, tok) => (
    <div key={label} style={{ flex: 1, textAlign: "center" }}>
      <div style={{ fontSize: 7, color: mk.c("textMuted"), textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 800, color: mk.c(tok), ...mk.glow(tok) }}>{val}</div>
    </div>
  );
  return Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      // Crew
      Card(mk, <>
        {Row([Text(mk, "Crew", { weight: 700, size: 12 }), spacer("s"), Chip(mk, "success", "On site 2/3")])}
        {SectionLabel(mk, "Refinishers")}
        {[["Alex M — On site", "success"], ["Jordan K — Clocked in", "success"], ["Sam T — Off", "textMuted"]].map(([n, tok], i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>{dot(mk, tok, 8)}<span style={{ color: mk.c("textPrimary"), fontSize: 10 }}>{n}</span></div>
        ))}
      </>, { key: "crew", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      // Money
      Card(mk, <>
        {Row([Text(mk, "Money", { weight: 700, size: 12 }), spacer("s"), segToggle(mk, ["Current", "Previous"], 0, "cp")])}
        {Text(mk, "Jul 20 – Jul 26", { tok: "textMuted", size: 8, key: "rg" })}
        <div style={{ display: "flex", gap: 4, marginTop: 5 }}>
          {statTile("In", "$8.4k", "success")}{statTile("Out", "$5.1k", "errorMoney")}{statTile("Net", "$3.3k", "textPrimary")}{statTile("Pot.", "$1.2k", "calTangerine")}
        </div>
      </>, { key: "money", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      // Volume + Action queue
      Card(mk, Row([Text(mk, "Jobs this period", { size: 10, tok: "textMuted" }), spacer("s"), Text(mk, "12 / 18", { size: 12, weight: 700, tok: "accentPrimary" })]), { key: "vol", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      Card(mk, Row([<span key="i" style={{ color: mk.c("warning"), fontSize: 12, ...mk.glow("warning") }}>◎</span>, Text(mk, "Pending approval: 3", { size: 10 }), spacer("s"), <span key="c" style={{ color: mk.c("accentPrimary"), fontSize: 13 }}>›</span>]), { key: "aq", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      // Business-metrics teaser (2026-08-07): three headline figures + a nav
      // affordance into the standalone metrics page.
      Card(mk, <>
        {Row([Text(mk, "Business metrics", { weight: 700, size: 11 })])}
        <div style={{ display: "flex", gap: 4, marginTop: 5 }}>
          {statTile("revenue", "$552k", "metricDeltaPositive")}
          {statTile("YoY", "+22.1%", "metricDeltaPositive")}
          {statTile("jobs", "587", "metricTileValue")}
        </div>
        {Row([spacer("s"), <span key="v" style={{ color: mk.c("accentPrimary"), fontSize: 9, ...mk.glow("accentPrimary") }}>View metrics ›</span>])}
      </>, { key: "bm", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
    ]),
  ], { nav: nav ? BottomNav(mk, nav, nav.length - 1) : null, overlay: withDrawer ? navDrawer(mk) : null });
};

// Standalone month calendar: year nav + Time Off / SIX pills, Jan–Dec month
// chips, weekday header, 6×7 grid with per-user event dots, and the admin
// two-row day header below (A|P|W + 1-Day|3-Day, then the date on its own).
// The SIX pill + A|P|W row are admin-shell chrome (2026-09-11); the same
// tokens paint them, so one mock covers both shells.
const yearNavPill = (mk, label, key) => (
  <span key={key} style={{ display: "flex", alignItems: "center", gap: 3, border: `1px solid ${mk.c("accentPrimary")}`, borderRadius: 10, padding: "2px 7px", color: mk.c("accentPrimary"), fontSize: 8, whiteSpace: "nowrap", ...mk.glow("accentPrimary") }}>{label}</span>
);
const monthCalendar = (mk, title) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      Card(mk, <>
        {Row([<span key="y" style={{ color: mk.c("textPrimary"), fontSize: 12, fontWeight: 700 }}>‹ 2026 ›</span>, spacer("s"),
          yearNavPill(mk, "⛱ Time Off", "to"), yearNavPill(mk, "SIX 👂", "six")], { gap: 4 })}
        <div style={{ display: "flex", gap: 3, overflow: "hidden", margin: "6px 0 4px" }}>
          {["Jan", "Feb", "Mar", "Apr", "May", "Jun"].map((m, i) => (
            <span key={m} style={{ fontSize: 8, padding: "2px 6px", borderRadius: 9, border: `1px solid ${i === 4 ? mk.c("accentPrimary") : mk.c("textMuted")}`, color: i === 4 ? mk.c("accentPrimary") : mk.c("textMuted"), ...(i === 4 ? mk.glow("accentPrimary") : null) }}>{m}</span>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, marginBottom: 2 }}>
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} style={{ textAlign: "center", fontSize: 6.5, color: mk.c("textMuted") }}>{d}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
          {Array.from({ length: 35 }).map((_, i) => {
            const today = i === 15, sel = i === 19;
            const dots = { 8: ["calPeacock"], 12: ["calTangerine", "calGrape"], 15: ["accentPrimary"], 19: ["calFlamingo"], 23: ["calPeacock", "calBanana"] }[i];
            return (
              <div key={i} style={{ aspectRatio: "1", borderRadius: 4, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7,
                background: today ? mk.c("surfaceListItem") : "transparent", border: sel ? `1.5px solid ${mk.c("accentPrimary")}` : "none", color: mk.c("textPrimary"), ...(sel ? mk.glow("accentPrimary") : null) }}>
                {i + 1 <= 31 ? i + 1 : ""}
                {dots && <span style={{ position: "absolute", bottom: 1, display: "flex", gap: 1 }}>{dots.map((t, k) => <span key={k} style={{ width: 3, height: 3, borderRadius: "50%", background: mk.c(t), ...mk.glow(t) }} />)}</span>}
              </div>
            );
          })}
        </div>
      </>, { key: "cal" }),
      Card(mk, <>
        {Row([segToggle(mk, ["A", "P", "W"], 0, "apw"), spacer("s"), segToggle(mk, ["1-Day", "3-Day"], 0, "d3")])}
        <div style={{ height: 5 }} />
        {Text(mk, "Wednesday, Jul 25", { size: 10, weight: 700 })}
      </>, { key: "day", flat: true }),
    ]),
  ]);

// Landscape day/week timeline: Month/Week pill rail (+ the admin A|P|W row
// under it, 2026-09-11) + 7-col day headers + the all-day chip row under them
// + dense user-colored blocks with green left stripe + green price; vacation
// hatch. Timed blocks start BELOW the all-day row (top offsets shifted +12).
const timeline = (mk, title) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      <div key="row" style={{ flex: 1, display: "flex", gap: 4, minHeight: 0 }}>
        {/* Month/Week pill rail + A|P|W row (admin shell) */}
        <div style={{ width: 46, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", gap: 3 }}>
            {[["Mo", 0], ["Wk", 1]].map(([lbl, sel]) => (
              <div key={lbl} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ fontSize: 6, color: mk.c("textMuted"), textAlign: "center" }}>{lbl}</div>
                {[0, 1, 2].map((r) => (
                  <div key={r} style={{ fontSize: 7, textAlign: "center", padding: "2px 0", borderRadius: 6, border: `1px solid ${r === sel ? mk.c("accentPrimary") : "transparent"}`, background: r === sel ? undefined : mk.c("surfaceListItem"), color: r === sel ? mk.c("accentPrimary") : mk.c("textPrimary"), ...(r === sel ? mk.glow("accentPrimary") : null) }}>{r + 1}</div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>{segToggle(mk, ["A", "P", "W"], 0, "apw")}</div>
        </div>
        {/* timeline */}
        <div style={{ flex: 1, background: FLAT_CARD_BG, borderRadius: 8, overflow: "hidden", position: "relative" }}>
          <div style={{ display: "flex", height: 16, borderBottom: `1px solid ${mk.c("textPrimary")}20` }}>
            {["Wed", "Thu", "Fri"].map((d, i) => (
              <div key={d} style={{ flex: 1, textAlign: "center", fontSize: 6.5, fontWeight: 700, color: i === 0 ? mk.c("accentPrimary") : mk.c("textMuted"), ...(i === 0 ? mk.glow("accentPrimary") : null) }}>{d}</div>
            ))}
          </div>
          {/* all-day (personal "SIX") chip row — creator colour, green stripe */}
          <div style={{ display: "flex", height: 12, borderBottom: `1px solid ${mk.c("textPrimary")}20`, background: `${mk.c("surfaceListItem")}66` }}>
            <div style={{ flex: 1, padding: "1px 2px" }}>
              <div style={{ height: 10, borderRadius: 3, background: mk.c("calGrape"), borderLeft: `2px solid ${mk.c("accentPrimary")}`, color: "#fff", fontSize: 6, fontWeight: 700, lineHeight: "10px", paddingLeft: 3, ...mk.glow("calGrape") }}>Dentist</div>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ flex: 1 }} />
          </div>
          <div style={{ position: "absolute", left: 4, top: 36, width: "28%", height: 34, borderRadius: 4, background: mk.c("calPeacock"), borderLeft: `2px solid ${mk.c("accentPrimary")}`, padding: 3, ...mk.glow("calPeacock") }}>
            <div style={{ color: "#fff", fontSize: 7, fontWeight: 700 }}>Oak St</div>
            <div style={{ color: mk.c("priceText"), fontSize: 7, fontWeight: 700, ...mk.glow("priceText") }}>$450</div>
          </div>
          <div style={{ position: "absolute", left: "36%", top: 52, width: "28%", height: 26, borderRadius: 4, background: mk.c("calTangerine"), borderLeft: `2px solid ${mk.c("accentPrimary")}`, padding: 3 }}>
            <div style={{ color: "#fff", fontSize: 7, fontWeight: 700 }}>Elm Ct</div>
          </div>
          {/* vacation hatch block */}
          <div style={{ position: "absolute", left: "68%", top: 38, width: "28%", height: 44, borderRadius: 4, background: `repeating-linear-gradient(45deg, ${mk.c("eventVacationNoUser")}, ${mk.c("eventVacationNoUser")} 3px, ${mk.c("error")}55 3px, ${mk.c("error")}55 6px)`, ...mk.glow("eventVacationNoUser") }} />
        </div>
      </div>,
    ]),
  ]);

// Refinisher "My Paycheck": period pills + Simple/Detailed toggle, summary card,
// colored section bars with job cards.
const myPaycheck = (mk, title) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      Pills(mk, ["This Period", "Last Period", "This Year", "Custom"], 0),
      Row([Text(mk, "7/24 – 8/6, 2026", { tok: "textMuted", size: 9 }), spacer("s"), segToggle(mk, ["Simple", "Detailed"], 0, "sd")], { key: "rg" }),
      Card(mk, <>
        <div style={{ display: "flex", gap: 8 }}>
          {[["Confirmed", "$1,240", "success"], ["Referral", "$50", "calGrape"], ["Advances", "−$100", "errorMoney"]].map(([l, v, t]) => (
            <div key={l} style={{ flex: 1 }}>
              <div style={{ fontSize: 7, color: mk.c("textMuted"), textTransform: "uppercase" }}>{l}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: mk.c(t), ...mk.glow(t) }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ borderTop: `1px solid ${dividerCss(mk)}`, marginTop: 6, paddingTop: 4 }}>{Text(mk, "Net: $1,190.00", { weight: 700, size: 12 })}</div>
      </>, { key: "sum" }),
      sectionBar(mk, "success", "Confirmed Jobs (3)", "sb1"),
      Card(mk, Row([<div key="a" style={{ flex: 1 }}><div style={{ color: mk.c("textPrimary"), fontSize: 10, fontWeight: 600 }}>Smith</div><div style={{ color: mk.c("textMuted"), fontSize: 8 }}>Thu · Invoice #1042</div></div>, Text(mk, "$450.00", { tok: "success", weight: 700, size: 11 })]), { key: "j1", flat: true }),
      sectionBar(mk, "errorMoney", "Advances (1)", "sb2"),
      Card(mk, Row([Text(mk, "Advance · Jul 18", { tok: "errorMoney", size: 10 }), spacer("s"), Text(mk, "−$100.00", { tok: "errorMoney", weight: 700, size: 11 })]), { key: "adv", flat: true }),
    ]),
  ]);

// Paycheck Calculator: %/amount splitter + Refinish|Maintenance weekday grid + totals.
const paycheckCalc = (mk) =>
  Screen(mk, [
    AppBar(mk, "Paycheck Calculator", { key: "ab" }),
    Body(mk, [
      Row([<div key="p" style={{ flex: 1 }}>{Input(mk, "Paycheck %", { value: "60" })}</div>, <div key="a" style={{ flex: 1 }}>{Input(mk, "Amount to divide", { value: "$900" })}</div>], { key: "top" }),
      Row([Text(mk, "Job divided by", { tok: "textMuted", size: 9 }), <span key="dd" style={{ border: `1px solid ${mk.c("inputBorder")}`, borderRadius: 4, padding: "1px 6px", color: mk.c("textPrimary"), fontSize: 9 }}>3 ▾</span>, Text(mk, "is $300/ea", { tok: "success", size: 9, weight: 700 })], { key: "div" }),
      <div key="hd" style={{ display: "flex", background: FLAT_CARD_BG, borderRadius: 6, padding: "3px 6px" }}>
        <span style={{ flex: 1 }} />
        <span style={{ flex: 1, textAlign: "center", fontSize: 9, fontWeight: 700, color: mk.c("textPrimary") }}>Refinish</span>
        <span style={{ flex: 1, textAlign: "center", fontSize: 9, fontWeight: 700, color: mk.c("textPrimary") }}>Maint.</span>
      </div>,
      ...["Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
        <div key={d} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 6px", borderRadius: 6, background: grad(mk, i % 2 ? "gradBrandStart" : "gradBrandRevStart", i % 2 ? "gradBrandEnd" : "gradBrandRevEnd") }}>
          <span style={{ flex: 1, fontSize: 9, color: mk.c("textPrimary") }}>{d}:</span>
          <span style={{ flex: 1, borderBottom: `1px solid ${mk.c("inputBorder")}`, fontSize: 9, color: mk.c("hintText"), textAlign: "center" }}>$</span>
          <span style={{ flex: 1, borderBottom: `1px solid ${mk.c("inputBorder")}`, fontSize: 9, color: mk.c("hintText"), textAlign: "center" }}>$</span>
        </div>
      )),
      Btn(mk, "Calculate", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", key: "calc" }),
    ], { gap: 5 }),
  ]);

// Business Ledger: tabs + year pills + search + Total Revenue / R·M·RM·HC count
// boxes + month-grouped cards with pinned purple→blue month banner.
const businessLedger = (mk) =>
  Screen(mk, [
    AppBar(mk, "Business Ledger", { key: "ab", actions: [1] }),
    Body(mk, [
      <div key="tb">{Tabs(mk, ["Ledger", "Refinishers", "Metrics"])}</div>,
      Pills(mk, ["2026", "2025", "2024"], 0),
      Card(mk, Row([<span key="s" style={{ flex: 1, color: mk.c("hintText"), fontSize: 9 }}>🔍 Search client, zip, refinisher…</span>]), { key: "srch", flat: true }),
      <div key="sum" style={{ display: "flex", gap: 4 }}>
        <div style={{ flex: 2, background: mk.c("surfaceListItem"), borderRadius: 6, padding: "4px 6px" }}>
          <div style={{ fontSize: 7, color: mk.c("textMuted") }}>Total Revenue</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: mk.c("success"), ...mk.glow("success") }}>$28.4k</div>
        </div>
        {[["R", "jobTypeR"], ["M", "jobTypeM"], ["RM", "jobTypeRM"], ["HC", "jobTypeHC"]].map(([l, t]) => (
          <div key={l} style={{ flex: 1, background: mk.c("surfaceListItem"), borderRadius: 6, padding: "3px 0", textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: mk.c(t), ...mk.glow(t) }}>{{ R: 12, M: 8, RM: 5, HC: 3 }[l]}</div>
            <div style={{ fontSize: 7, color: mk.c("textMuted") }}>{l}</div>
          </div>
        ))}
      </div>,
      Card(mk, Row([Text(mk, "July 2026", { size: 11, weight: 700 }), spacer("s"), Text(mk, "$8,420", { tok: "success", size: 12, weight: 700 })]), { key: "mo", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      ...[["Jul 24", "jobTypeM", "M", "Smith"], ["Jul 22", "jobTypeR", "R", "Nguyen"], ["Jul 20", "jobTypeRM", "RM", "Patel"]].map(([date, tok, b, name], i) =>
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", background: mk.c("surfaceListItem"), borderRadius: 8 }}>
          <span style={{ background: `${mk.c(tok)}`, color: "#fff", fontSize: 8, fontWeight: 700, padding: "2px 5px", borderRadius: 4, ...mk.glow(tok) }}>{b}</span>
          <div style={{ flex: 1 }}><div style={{ color: mk.c("textPrimary"), fontSize: 10, fontWeight: 600 }}>{name}</div><div style={{ color: mk.c("textMuted"), fontSize: 7.5 }}>{date} · 78704</div></div>
          <span style={{ color: mk.c("success"), fontSize: 11, fontWeight: 700, ...mk.glow("success") }}>$450</span>
        </div>
      ),
    ], { gap: 6 }),
  ]);

// Business metrics: year pills + KPI tiles + annual bars (with a hatched
// projected bar) + seasonality line + job-type donut + ranked crew bars.
// Consumes EVERY metric* token so none of them is invisible in the console.
const businessMetrics = (mk) =>
  Screen(mk, [
    AppBar(mk, "Metrics", { key: "ab" }),
    Body(mk, [
      Pills(mk, ["All Years", "2026", "2025"], 0),
      <div key="tiles">{MockStatTile(mk)}</div>,
      Card(mk, <>
        {Text(mk, "Annual Revenue", { size: 10, weight: 700, key: "t" })}
        {MockBarChart(mk, { key: "bars" })}
      </>, { key: "annual", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      Card(mk, <>
        {Text(mk, "Seasonality", { size: 10, weight: 700, key: "t" })}
        {MockLineChart(mk, { key: "line" })}
      </>, { key: "season", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      Card(mk, <>
        {Text(mk, "Job Type Mix", { size: 10, weight: 700, key: "t" })}
        {MockDonut(mk, { key: "donut" })}
      </>, { key: "mix", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      Card(mk, <>
        {Text(mk, "Crew by Revenue", { size: 10, weight: 700, key: "t" })}
        {MockRankedBars(mk, { key: "ranked" })}
      </>, { key: "crew", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
    ], { gap: 6 }),
  ]);

// Payroll report: dropdown mode + preset pills + Add Advance/Snapshot + grand
// total card + per-refinisher accordion.
const payrollReport = (mk, title) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab", actions: [1] }),
    Body(mk, [
      Row([<span key="dd" style={{ flex: 1, border: `1px solid ${mk.c("inputBorder")}`, borderRadius: 6, padding: "4px 8px", color: mk.c("textPrimary"), fontSize: 10 }}>Payroll Report ▾</span>], { key: "mode" }),
      Pills(mk, ["Last Period", "This Period"], 1),
      Row([Btn(mk, "+ Advance", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", key: "a" }), Btn(mk, "Snapshot", { a: "gradBrandRevStart", b: "gradBrandRevEnd", key: "s" })], { key: "acts" }),
      Card(mk, Row([Text(mk, "Total Payroll", { size: 11, weight: 600 }), spacer("s"), Text(mk, "$5,120.00", { tok: "success", size: 13, weight: 800 })]), { key: "gt", gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] }),
      ...[["Alex M", "calPeacock", "12 jobs · 34%", "$1,315"], ["Jordan K", "calTangerine", "9 jobs · 26%", "$980"]].map(([n, c, meta, tot], i) => (
        <div key={i} style={{ borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", background: grad(mk, "gradBrandRevStart", "gradBrandRevEnd") }}>
            {dot(mk, c, 10)}
            <div style={{ flex: 1 }}><div style={{ color: mk.c("textPrimary"), fontSize: 10, fontWeight: 700 }}>{n}</div><div style={{ color: mk.c("textMuted"), fontSize: 7.5 }}>{meta}</div></div>
            <span style={{ color: mk.c("success"), fontSize: 11, fontWeight: 800, ...mk.glow("success") }}>{tot}</span>
            <span style={{ color: mk.c("textMuted"), fontSize: 10 }}>▾</span>
          </div>
        </div>
      )),
    ], { gap: 6 }),
  ]);

// Inventory distribution report: per-user expandable rows with a date stepper +
// item quantity rows.
const inventoryReport = (mk) =>
  Screen(mk, [
    AppBar(mk, "Inv. Distribution", { key: "ab", actions: [1] }),
    Body(mk, [
      Pills(mk, ["This Month", "This Year"], 0),
      ...[["Alex M", "calPeacock", true], ["Jordan K", "calTangerine", false], ["Sam T", "calGrape", false]].map(([n, c, open], i) => (
        <div key={i} style={{ borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: grad(mk, "gradBrandStart", "gradBrandEnd") }}>{dot(mk, c, 10)}<span style={{ flex: 1, color: mk.c("textPrimary"), fontSize: 10, fontWeight: 700 }}>{n}</span><span style={{ color: mk.c("textMuted"), fontSize: 10 }}>{open ? "▾" : "▸"}</span></div>
          {open && (
            <div style={{ background: FLAT_CARD_BG, padding: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ color: mk.c("accentPrimary"), fontSize: 11 }}>‹</span>
                <span style={{ color: mk.c("textPrimary"), fontSize: 9, fontWeight: 700 }}>Jul 25, 2026</span>
                <span style={{ color: mk.c("accentPrimary"), fontSize: 11 }}>›</span>
              </div>
              {[["Foam Wedges", "12", "40"], ["Weather Strip", "4", "18"]].map(([it, d, tot], k) => (
                <div key={k} style={{ display: "flex", alignItems: "center", padding: "3px 2px", borderTop: `1px solid ${dividerCss(mk)}` }}>
                  <span style={{ flex: 5, color: mk.c("textPrimary"), fontSize: 9 }}>{it}</span>
                  <span style={{ flex: 2, textAlign: "center", color: mk.c("textPrimary"), fontSize: 9, fontWeight: 700 }}>{d}</span>
                  <span style={{ flex: 3, textAlign: "right", color: mk.c("accentPrimary"), fontSize: 9, ...mk.glow("accentPrimary") }}>{tot}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )),
    ], { gap: 6 }),
  ]);

// Shopping list: Buy List/All toggle + rows with Inv/Max/Req stat pills + green
// "To Buy" + sticky total footer.
const shoppingList = (mk) =>
  Screen(mk, [
    AppBar(mk, "Shopping List", { key: "ab" }),
    Body(mk, [
      Row([Text(mk, "Items needing purchase", { tok: "textMuted", size: 8.5 }), spacer("s"), segToggle(mk, ["Buy List", "All"], 0, "ba")], { key: "hd" }),
      ...[["Foam Wedges", "10", "50", "8", "warningAlt", "12"], ["Hinges", "24", "60", "30", "error", "36"], ["Weather Strip", "6", "20", "2", "warningAlt", "14"]].map((r, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 8px", background: mk.c("surfaceListItem"), borderRadius: 8 }}>
          <span style={{ flex: 1, color: mk.c("textPrimary"), fontSize: 10, fontWeight: 500 }}>{r[0]}</span>
          <span style={{ fontSize: 7, color: mk.c("textMuted"), border: `1px solid ${mk.c("textMuted")}`, borderRadius: 8, padding: "1px 4px" }}>Inv {r[1]}</span>
          <span style={{ fontSize: 7, color: mk.c(r[4]), border: `1px solid ${mk.c(r[4])}`, borderRadius: 8, padding: "1px 4px", ...mk.glow(r[4]) }}>Req {r[3]}</span>
          <span style={{ color: mk.c("accentPrimary"), fontSize: 11, fontWeight: 800, ...mk.glow("accentPrimary") }}>{r[5]}</span>
        </div>
      )),
      <div key="ft" style={{ marginTop: "auto", display: "flex", padding: "6px 8px", background: FLAT_CARD_BG, borderRadius: 8, borderTop: `1px solid ${dividerCss(mk)}` }}>
        <span style={{ flex: 1, color: mk.c("textPrimary"), fontSize: 9 }}>Total to fill shop</span>
        <span style={{ color: mk.c("success"), fontSize: 10, fontWeight: 800, ...mk.glow("success") }}>$248.00</span>
      </div>,
    ], { gap: 5 }),
  ]);

// Reports overview (mode-select landing): dropdown + range/pull buttons + a
// representative payroll panel.
const reports = (mk, title) => payrollReport(mk, title);

// Post Job Form: glassy cards on brand gradient — header(client+status pill),
// Hardware toggles, Sweep & Weather Strip, Before/After photo groups, Payment
// radios, Save Draft + Submit for Review.
const jobForm = (mk) =>
  Screen(mk, [
    AppBar(mk, "Post Job Form", { key: "ab" }),
    Body(mk, [
      Row([<div key="c" style={{ flex: 1 }}><div style={{ fontSize: 7, color: mk.c("textMuted") }}>Client</div><div style={{ color: mk.c("textPrimary"), fontSize: 11, fontWeight: 700 }}>Smith</div></div>, dot(mk, "calPeacock", 12), Chip(mk, "statusDraft", "Draft")], { key: "hdr" }),
      Card(mk, <>
        {SectionLabel(mk, "Hardware")}
        {Toggle(mk, "Installed New Hardware?", true)}
        <div style={{ height: 6 }} />
        {Input(mk, "Handle Operation", { value: "Lever" })}
      </>, { key: "hw" }),
      Card(mk, <>
        {SectionLabel(mk, "Sweep & Weather Strip")}
        {Toggle(mk, "Replaced Door Sweep?", true)}
        <div style={{ height: 6 }} />
        {Toggle(mk, "Changed Weather Strip?", false)}
        <div style={{ height: 6 }} />
        {Input(mk, "Foam Wedges", { value: "4" })}
      </>, { key: "sw" }),
      Card(mk, <>
        {SectionLabel(mk, "Before / After Photos — Group 1")}
        <div style={{ display: "flex", gap: 6 }}>
          {[["Before", true], ["After", true]].map(([lbl, filled]) => (
            <div key={lbl} style={{ flex: 1 }}>
              <div style={{ aspectRatio: "3/4", borderRadius: 6, background: filled ? grad(mk, "gradBrandStart", "gradBrandEnd") : mk.c("surfaceListItem") }} />
              <div style={{ textAlign: "center", fontSize: 7, color: mk.c("textMuted"), marginTop: 1 }}>{lbl}</div>
            </div>
          ))}
        </div>
      </>, { key: "ba" }),
      Card(mk, <>
        {SectionLabel(mk, "Payment")}
        {Radio(mk, "Cash", true)}<div style={{ height: 4 }} />{Radio(mk, "Check", false)}<div style={{ height: 4 }} />{Radio(mk, "Call For Payment", false)}
      </>, { key: "pay" }),
      Row([Btn(mk, "Save Draft", { a: "gradDisabledStart", b: "gradDisabledEnd", key: "sd" }), Btn(mk, "Submit for Review", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", key: "su" })], { key: "btns" }),
    ]),
  ]);

// Login: gradient-bordered card, underline Email/Password (Face ID in suffix),
// "Use Face ID / PIN" switch, Submit, Forgot password.
const login = (mk) => (
  <div style={{ height: "100%", background: grad(mk, "gradBrandStart", "gradBrandEnd"), display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
    <div style={{ width: "100%", borderRadius: 14, padding: 2, background: grad(mk, "gradBrandStart", "gradBrandEnd"), ...mk.glow("gradBrandStart", "gradBrandEnd") }}>
      <div style={{ background: mk.c("surfaceLight"), borderRadius: 12, padding: 16, ...mk.glow("surfaceLight") }}>
        <div style={{ textAlign: "center", color: mk.c("textOnLight"), fontWeight: 700, fontSize: 13, marginBottom: 14, ...mk.glow("textOnLight") }}>Biggin's Door Refinishing</div>
        <div style={{ borderBottom: `1px solid ${mk.c("inputBorder")}`, padding: "6px 0", marginBottom: 12 }}>
          <span style={{ color: mk.c("labelText"), fontSize: 10 }}>Email:</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${mk.c("inputBorder")}`, padding: "6px 0", marginBottom: 14 }}>
          <span style={{ flex: 1, color: mk.c("labelText"), fontSize: 10 }}>Password:</span>
          <span style={{ fontSize: 10, color: mk.c("textMuted") }}>👁</span>
          <span style={{ marginLeft: 6, width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${mk.c("accentPrimary")}`, ...mk.glow("accentPrimary") }} />
        </div>
        {Btn(mk, "Submit", { a: "gradBrandStart", b: "gradBrandEnd", key: "sub" })}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
          <span style={{ color: mk.c("textOnLight"), opacity: 0.7, fontSize: 9, flex: 1 }}>Use Face ID / PIN</span>
          <span style={{ width: 30, height: 16, borderRadius: 8, background: mk.c("switchOnTrack"), position: "relative", ...mk.glow("switchOnTrack") }}>
            <span style={{ position: "absolute", top: 2, right: 2, width: 12, height: 12, borderRadius: "50%", background: mk.c("switchOnThumb") }} />
          </span>
        </div>
        <div style={{ textAlign: "center", marginTop: 10, color: mk.c("textLink"), fontSize: 9, textDecoration: "underline", ...mk.glow("textLink") }}>Forgot password?</div>
      </div>
    </div>
  </div>
);

// Settings: section headers + card-style rows (toggles + accent selector values).
const settings = (mk) => {
  const row = (label, right, key) => (
    <div key={key} style={{ display: "flex", alignItems: "center", padding: "8px 10px", background: mk.c("surfaceListItem"), borderRadius: 8, marginBottom: 6, ...mk.glow("surfaceListItem") }}>
      <span style={{ flex: 1, color: mk.c("textPrimary"), fontSize: 11, ...mk.glow("textPrimary") }}>{label}</span>{right}
    </div>
  );
  const sw = (on) => (
    <span style={{ width: 30, height: 16, borderRadius: 8, background: mk.c(on ? "switchOnTrack" : "switchOffTrack"), position: "relative", ...mk.glow(on ? "switchOnTrack" : "switchOffTrack") }}>
      <span style={{ position: "absolute", top: 2, [on ? "right" : "left"]: 2, width: 12, height: 12, borderRadius: "50%", background: mk.c(on ? "switchOnThumb" : "switchOffThumb") }} />
    </span>
  );
  const chev = (val) => <span style={{ color: mk.c("accentPrimary"), fontSize: 10, ...mk.glow("accentPrimary") }}>{val} ›</span>;
  return Screen(mk, [
    AppBar(mk, "Settings", { key: "ab" }),
    Body(mk, [
      SectionLabel(mk, "Account", { key: "s1" }), row("Change Password", chev(""), "r1"),
      SectionLabel(mk, "Appearance", { key: "s2" }), row("Dark Mode", sw(false), "r2"),
      SectionLabel(mk, "Maps", { key: "s3" }), row("Maps App", chev("Apple"), "r3"),
      SectionLabel(mk, "Push Notifications", { key: "s4" }), row("Inventory Requests", sw(true), "r4"), row("Calendar Assignments", sw(true), "r5"),
      SectionLabel(mk, "Developer", { key: "s5" }), row("Use Test Database", sw(false), "r6"),
    ], { gap: 0 }),
  ]);
};

// Inbox: My Inbox toggle + pagination + green month divider + email rows with
// flag badges (! orange circle, !! red triangle) + green timestamp.
const inbox = (mk) =>
  Screen(mk, [
    AppBar(mk, "Email Inbox", { key: "ab" }),
    Body(mk, [
      Row([spacer("l"), Text(mk, "My Inbox", { tok: "textMuted", size: 9 }), <span key="sw" style={{ width: 26, height: 14, borderRadius: 7, background: mk.c("switchOnTrack"), position: "relative", ...mk.glow("switchOnTrack") }}><span style={{ position: "absolute", top: 2, right: 2, width: 10, height: 10, borderRadius: "50%", background: mk.c("switchOnThumb") }} /></span>], { key: "tg" }),
      Row([Text(mk, "24 Emails · Page 1 of 3", { tok: "textMuted", size: 8 }), spacer("s"), <span key="c" style={{ color: mk.c("accentPrimary"), fontSize: 10 }}>‹ ›  ⟳</span>, <span key="sel" style={{ color: mk.c("accentPrimary"), fontSize: 9, marginLeft: 4, ...mk.glow("accentPrimary") }}>Select</span>], { key: "pg" }),
      // Multi-select contextual bar. On the real page it REPLACES the two rows
      // above; both are drawn here so every token it uses stays eyeballable.
      <div key="bulk" style={{ display: "flex", flexDirection: "column", gap: 3, padding: "4px 6px", background: mk.c("surfaceListItem"), borderRadius: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: mk.c("textPrimary"), fontSize: 10 }}>✕</span>
          <span style={{ flex: 1, color: mk.c("textPrimary"), fontSize: 9, fontWeight: 600 }}>3 selected</span>
          <span style={{ color: mk.c("accentPrimary"), fontSize: 9, ...mk.glow("accentPrimary") }}>All</span>
          <span style={{ color: mk.c("textMuted"), fontSize: 9 }}>|</span>
          <span style={{ color: mk.c("accentPrimary"), fontSize: 9, ...mk.glow("accentPrimary") }}>None</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {["Read", "Unread", "Flag ▾", "Archive"].map((a) => (
            <span key={a} style={{ color: mk.c("accentPrimary"), fontSize: 9, ...mk.glow("accentPrimary") }}>{a}</span>
          ))}
        </div>
      </div>,
      <div key="md" style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ flex: 1, height: 1, background: mk.c("accentPrimary"), opacity: 0.5 }} /><span style={{ color: mk.c("accentPrimary"), fontSize: 8, ...mk.glow("accentPrimary") }}>July</span><span style={{ flex: 1, height: 1, background: mk.c("accentPrimary"), opacity: 0.5 }} /></div>,
      ...[["New quote request", true, ["warningAlt", "!", "○"]], ["Invoice reply — Jane", false, null], ["Referral inquiry", true, ["error", "!!", "▲"]], ["Schedule change", false, null]].map(([subj, unread, flag], i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: mk.c("surfaceListItem"), borderRadius: 8, opacity: unread ? 1 : 0.7, border: `1px solid ${i === 0 ? mk.c("accentPrimary") : flag ? mk.c(flag[0]) : "transparent"}` }}>
          {/* Selection-mode checkbox: row 0 is drawn checked so the three
              checkbox tokens are visible in the preview. */}
          <span style={{ width: 13, height: 13, flex: "0 0 auto", borderRadius: 3, border: `1.5px solid ${mk.c(i === 0 ? "checkboxActive" : "checkboxOff")}`, background: i === 0 ? mk.c("checkboxActive") : "transparent", color: mk.c("checkboxCheck"), fontSize: 9, lineHeight: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{i === 0 ? "✓" : ""}</span>
          {flag && <span style={{ width: 15, height: 15, borderRadius: flag[0] === "error" ? 2 : "50%", background: mk.c(flag[0]), color: "#fff", fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, ...mk.glow(flag[0]) }}>{flag[1]}</span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: mk.c("textPrimary"), fontSize: 10, fontWeight: unread ? 700 : 400 }}>client@x.com</div>
            <div style={{ color: mk.c("textMuted"), fontSize: 8.5 }}>{subj}</div>
          </div>
          <span style={{ color: mk.c("success"), fontSize: 8, ...mk.glow("success") }}>9:41a</span>
        </div>
      )),
    ], { gap: 5 }),
  ]);

// Sent Emails: descriptive toggle card + To:/From:/Subject:/att: prefixed rows,
// att count red when > 0.
const sentEmails = (mk) =>
  Screen(mk, [
    AppBar(mk, "Sent Emails", { key: "ab" }),
    Body(mk, [
      Card(mk, Row([<div key="t" style={{ flex: 1 }}><div style={{ color: mk.c("textPrimary"), fontSize: 10, fontWeight: 700 }}>Show My Sent</div><div style={{ color: mk.c("textMuted"), fontSize: 8 }}>Emails from your prefix</div></div>, <span key="sw" style={{ width: 30, height: 16, borderRadius: 8, background: mk.c("switchOnTrack"), position: "relative", ...mk.glow("switchOnTrack") }}><span style={{ position: "absolute", top: 2, right: 2, width: 12, height: 12, borderRadius: "50%", background: mk.c("switchOnThumb") }} /></span>]), { key: "tg", flat: true }),
      Text(mk, "18 Emails · Page 1 of 2", { tok: "textMuted", size: 8, key: "pg" }),
      ...[["jane@x.com", "office@bdr.com", "Re: Invoice 1042", "2"], ["bob@z.com", "office@bdr.com", "Quote follow-up", "0"]].map((r, i) => (
        <div key={i} style={{ padding: "6px 8px", background: mk.c("surfaceListItem"), borderRadius: 8 }}>
          <div style={{ display: "flex" }}><span style={{ flex: 1, color: mk.c("textPrimary"), fontSize: 9.5 }}>To: {r[0]}</span><span style={{ color: mk.c("success"), fontSize: 8, ...mk.glow("success") }}>9:41a</span></div>
          <div style={{ color: mk.c("textMuted"), fontSize: 8.5 }}>From: {r[1]}</div>
          <div style={{ color: mk.c("textPrimary"), fontSize: 9 }}>Subject: {r[2]}</div>
          <div style={{ color: mk.c(r[3] === "0" ? "textMuted" : "error"), fontSize: 8, ...(r[3] === "0" ? null : mk.glow("error")) }}>att: {r[3]}</div>
        </div>
      )),
    ], { gap: 5 }),
  ]);

// User Management: collapsible invite panel + user cards (color dot + name label,
// Email/Perm/Pay% lines, edit + deactivate, Inactive pill).
const userMgmt = (mk) =>
  Screen(mk, [
    AppBar(mk, "User Management", { key: "ab" }),
    Body(mk, [
      Card(mk, Row([Text(mk, "Invite New User", { weight: 700, size: 11 }), spacer("s"), <span key="c" style={{ color: mk.c("textMuted"), fontSize: 11 }}>▾</span>]), { key: "inv", flat: true }),
      ...[["Alex M", "refinisher", "calPeacock", "Peacock", "Pay %: 60"], ["Jordan K", "office", "calTangerine", "Tangerine", "Wage: $22.00/hr"], ["Sam T", "admin", "calGrape", "Grape", "Wage: $30.00/hr"]].map((u, i) =>
        Card(mk, <>
          {Row([Text(mk, u[0], { weight: 700, size: 11 }), spacer("s"), i === 2 ? <span key="ia" style={{ background: `${mk.c("error")}44`, color: mk.c("error"), fontSize: 7, padding: "1px 6px", borderRadius: 8, ...mk.glow("error") }}>Inactive</span> : null])}
          <div style={{ color: mk.c("success"), fontSize: 8.5, ...mk.glow("success") }}>Perm: {u[1]}</div>
          <div style={{ color: mk.c("success"), fontSize: 8.5 }}>{u[4]}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            {dot(mk, u[2], 14)}<span style={{ color: mk.c("textMuted"), fontSize: 8 }}>{u[3]}</span>{spacer("s")}
            <span style={{ color: mk.c("accentPrimary"), fontSize: 11, ...mk.glow("accentPrimary") }}>✎</span>
            <span style={{ color: mk.c("error"), fontSize: 11, ...mk.glow("error") }}>⊘</span>
          </div>
        </>, { key: i, gradKey: ["gradBrandStart", "gradBrandEnd"] })
      ),
    ], { gap: 8 }),
  ]);

const consoleScreen = (mk, title) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      <div key="b1" style={{ alignSelf: "flex-start", maxWidth: "82%", background: mk.c("surfaceListItem"), borderRadius: 8, padding: 8, color: mk.c("textPrimary"), fontSize: 10, ...mk.glow("surfaceListItem") }}>Running health report on the EC2 box…</div>,
      <div key="b2" style={{ alignSelf: "flex-end", maxWidth: "82%", background: grad(mk, "gradBrandStart", "gradBrandEnd"), borderRadius: 8, padding: 8, color: mk.c("textPrimary"), fontSize: 10, ...mk.glow("gradBrandStart", "gradBrandEnd") }}>Check pm2 + disk usage</div>,
      <div key="b3" style={{ alignSelf: "flex-start", color: mk.c("warning"), fontSize: 10, fontFamily: "monospace", ...mk.glow("warning") }}>› tool: exec — nginx OK</div>,
      <div key="b4" style={{ alignSelf: "flex-start", color: mk.c("error"), fontSize: 10, fontFamily: "monospace", ...mk.glow("error") }}>! disk 82%</div>,
    ], { gap: 6 }),
  ]);

// Process Payment: 3-step Amount/Payment/Confirm tracker + twin Invoice#/Amount
// boxes + "in the amount of $X" + white green-bordered Stripe card + Prev/Submit.
const payment = (mk) =>
  Screen(mk, [
    AppBar(mk, "Process Payment", { key: "ab" }),
    Body(mk, [
      // step tracker
      <div key="tk">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {["Amount", "Payment", "Confirm"].map((s, i) => (
            <span key={s} style={{ fontSize: 9, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? mk.c("accentPrimary") : mk.c("textMuted"), ...(i === 0 ? mk.glow("accentPrimary") : null) }}>{s}</span>
          ))}
        </div>
        <div style={{ height: 2, background: mk.c("textMuted"), borderRadius: 2, marginTop: 3, position: "relative" }}>
          <span style={{ position: "absolute", left: 0, top: 0, width: "12%", height: "100%", background: mk.c("accentPrimary"), ...mk.glow("accentPrimary") }} />
        </div>
      </div>,
      Row([
        <div key="inv" style={{ flex: 1, border: `1px solid ${mk.c("accentPrimary")}`, borderRadius: 8, padding: 6, ...mk.glow("accentPrimary") }}><div style={{ fontSize: 7, color: mk.c("labelText") }}>Invoice #</div><div style={{ color: mk.c("textPrimary"), fontSize: 11 }}>1042</div></div>,
        <div key="amt" style={{ flex: 1, border: `1px solid ${mk.c("accentPrimary")}`, borderRadius: 8, padding: 6 }}><div style={{ fontSize: 7, color: mk.c("labelText") }}>Enter Amount</div><div style={{ color: mk.c("textPrimary"), fontSize: 11 }}>$450.00</div></div>,
      ], { key: "boxes" }),
      <div key="sent" style={{ fontSize: 9, color: mk.c("textPrimary"), lineHeight: 1.4 }}>Processing a payment for <b style={{ color: mk.c("accentPrimary") }}>Smith</b> in the amount of <b style={{ color: mk.c("accentPrimary") }}>$450.00</b></div>,
      <div key="card" style={{ background: "#fff", border: `1px solid ${mk.c("accentPrimary")}`, borderRadius: 8, padding: "10px", color: "#999", fontSize: 10, ...mk.glow("accentPrimary") }}>•••• •••• •••• 4242</div>,
      Row([Btn(mk, "Prev", { a: "gradDisabledStart", b: "gradDisabledEnd", key: "p" }), Btn(mk, "Submit", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", key: "s" })], { key: "nav" }),
    ]),
  ]);

// `surfaceTok` (2026-09-11): the schedule dialogs (Time Off + Personal Event)
// paint `surfaceScheduleModal` instead of the generic `surfaceModal`, so the
// token is visible — and therefore verifiable — in the console. `bordered`
// (2026-09-14): those two cards (and their date/time pickers) carry the app's
// 1 px `textPrimary` hairline, same as BdrDialogShell.
const modal = (mk, title, inner, { surfaceTok = "surfaceModal", confirm = "Confirm", bordered = false } = {}) => (
  <div style={{ height: "100%", background: mk.c("barrierScrim"), display: "flex", alignItems: "center", justifyContent: "center", padding: 14, ...mk.glow("barrierScrim") }}>
    <div style={{ width: "100%", background: mk.c(surfaceTok), borderRadius: 12, padding: 14, border: bordered ? `1px solid ${mk.c("textPrimary")}` : "none", ...mk.glow(surfaceTok) }}>
      <div style={{ color: mk.c("textPrimary"), fontWeight: 600, fontSize: 13, marginBottom: 10, ...mk.glow("textPrimary") }}>{title}</div>
      {inner}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
        <span style={{ color: mk.c("textMuted"), fontSize: 11 }}>Cancel</span>
        <span style={{ color: mk.c("accentPrimary"), fontSize: 11, fontWeight: 600, ...mk.glow("accentPrimary") }}>{confirm}</span>
      </div>
    </div>
  </div>
);

// Personal ("SIX") event dialog: Label; Date + the All Day switch on ONE row;
// Start + End on one row (timed mode, 2026-09-14 compact layout); optional
// Note — on surfaceScheduleModal.
const outlinePill = (mk, label, key) => (
  <span key={key} style={{ flex: 1, textAlign: "center", border: `1px solid ${mk.c("textPrimary")}55`, borderRadius: 9, padding: "2px 6px", color: mk.c("textPrimary"), fontSize: 9, whiteSpace: "nowrap" }}>{label}</span>
);
const boldLabel = (mk, label, key) => (
  <span key={key} style={{ color: mk.c("textPrimary"), fontSize: 9, fontWeight: 700, whiteSpace: "nowrap", ...mk.glow("textPrimary") }}>{label}</span>
);
const personalEventModal = (mk) =>
  modal(mk, "Personal Event", <>
    {Input(mk, "Label", { value: "Dentist", focused: true })}
    <div style={{ height: 9 }} />
    {Row([boldLabel(mk, "Date", "dl"), outlinePill(mk, "Mon, Sep 14", "dv"), boldLabel(mk, "All Day", "al"),
      <span key="sw" style={{ width: 26, height: 14, borderRadius: 7, background: mk.c("switchOffTrack"), position: "relative", display: "inline-block", flexShrink: 0, ...mk.glow("switchOffTrack") }}>
        <span style={{ position: "absolute", top: 2, left: 2, width: 10, height: 10, borderRadius: "50%", background: mk.c("switchOffThumb") }} />
      </span>])}
    <div style={{ height: 7 }} />
    {Row([boldLabel(mk, "Start", "sl"), outlinePill(mk, "8:00 AM", "sv"), <span key="g" style={{ width: 6 }} />, boldLabel(mk, "End", "el"), outlinePill(mk, "5:00 PM", "ev")])}
    <div style={{ height: 9 }} />
    {/* Repeat block (2026-09-20). Shown in its ON state so the rows it
        reveals are themeable: the switch, the interval + frequency pills,
        the derived summary (textMuted) and the Ends pill. There is no
        weekday picker by design — the pattern day comes from Date above. */}
    {Row([boldLabel(mk, "Repeats", "rl"),
      <span key="rsw" style={{ width: 26, height: 14, borderRadius: 7, background: mk.c("accentPrimary"), position: "relative", display: "inline-block", flexShrink: 0, ...mk.glow("accentPrimary") }}>
        <span style={{ position: "absolute", top: 2, right: 2, width: 10, height: 10, borderRadius: "50%", background: mk.c("switchOffThumb") }} />
      </span>,
      <span key="rsum" style={{ marginLeft: "auto", color: mk.c("textMuted"), fontSize: 8 }}>Every 2 weeks on Monday</span>])}
    <div style={{ height: 7 }} />
    {Row([boldLabel(mk, "Every", "il"), outlinePill(mk, "2", "iv"), outlinePill(mk, "weeks", "fv")])}
    <div style={{ height: 7 }} />
    {Row([boldLabel(mk, "Ends", "el2"), outlinePill(mk, "Dec 31, 2026", "ev2")])}
    <div style={{ height: 9 }} />
    {Input(mk, "Note (optional)", {})}
  </>, { surfaceTok: "surfaceScheduleModal", confirm: "Save", bordered: true });

// Client Referral: intro + form fields + consent checkbox + address heading +
// "Use My Current Location" + photo preview + Submit. One gradient panel.
const clientReferral = (mk) =>
  Screen(mk, [
    AppBar(mk, "Client Referral", { key: "ab" }),
    Body(mk, [
      Text(mk, "Refer a client by entering their details:", { tok: "textMuted", size: 9, key: "intro" }),
      Card(mk, <>
        {Input(mk, "Name", { value: "Jane Doe", focused: true })}
        <div style={{ height: 8 }} />
        {Input(mk, "Email", { value: "jane@x.com" })}
        <div style={{ height: 8 }} />
        <div style={{ display: "flex", gap: 6 }}><div style={{ flex: 2 }}>{Input(mk, "Phone", { value: "" })}</div><div style={{ flex: 1 }}>{Input(mk, "Type", { value: "Cell" })}</div></div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>{checkbox(mk, true)}<span style={{ color: mk.c("textPrimary"), fontSize: 9 }}>I have consent to contact the client.</span></div>
      </>, { key: "form", gradKey: ["gradBrandStart", "gradBrandEnd"] }),
      Text(mk, "Address", { weight: 700, size: 11, key: "ah" }),
      Btn(mk, "Use My Current Location", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", icon: true, key: "loc" }),
      Row([<div key="ph" style={{ width: 60, height: 60, borderRadius: 8, background: mk.c("surfaceListItem"), ...mk.glow("surfaceListItem") }} />, <div key="b" style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>{Btn(mk, "Take Photo", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd" })}{Btn(mk, "Select Photo", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd" })}</div>], { key: "photo" }),
      Btn(mk, "Submit Referral", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", key: "sub" }),
    ]),
  ]);

// Inventory Request: Freq./A-Z sort toggle + checkbox item rows with quantity
// stepper + Submit Request bottom button.
const inventoryRequest = (mk) =>
  Screen(mk, [
    AppBar(mk, "Inventory Request", { key: "ab" }),
    Body(mk, [
      Row([spacer("l"), segToggle(mk, ["Freq.", "A-Z"], 0, "srt")], { key: "hd" }),
      ...[["Foam Wedges", "4", true], ["Weather Strip", "2", true], ["Hinges", "0", false]].map((r, i) =>
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", background: r[2] ? mk.c("surfaceListItem") : "transparent", border: r[2] ? `1px solid ${mk.c("textPrimary")}30` : "1px solid transparent", borderRadius: 8 }}>
          {checkbox(mk, r[2])}
          <span style={{ flex: 1, color: mk.c("textPrimary"), fontSize: 11, fontWeight: r[2] ? 600 : 400 }}>{r[0]}</span>
          {r[2] ? <>
            <span style={{ color: mk.c("accentPrimary"), fontSize: 14, fontWeight: 700, ...mk.glow("accentPrimary") }}>−</span>
            <span style={{ color: mk.c("textPrimary"), fontSize: 11, width: 14, textAlign: "center" }}>{r[1]}</span>
            <span style={{ color: mk.c("accentPrimary"), fontSize: 14, fontWeight: 700 }}>+</span>
          </> : <span style={{ color: mk.c("textMuted"), fontSize: 9 }}>tap to add</span>}
        </div>
      ),
      <span key="sp" style={{ flex: 1 }} />,
      Btn(mk, "Submit Request", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", key: "b" }),
    ], { gap: 6 }),
  ]);

// Go By Follow-Up: Client line + notes + photo tiles (red cancel) + Camera/Gallery
// outlined + solid-green File Follow-Up Task.
const goByFollowup = (mk) =>
  Screen(mk, [
    AppBar(mk, "Go By Follow-Up", { key: "ab" }),
    Body(mk, [
      Text(mk, "Client: Smith", { weight: 700, size: 11, key: "cl" }),
      Card(mk, Input(mk, "Notes from the visit (optional)", { value: "Left brochure, will call back", focused: true }), { key: "n" }),
      Text(mk, "Photos (optional)", { weight: 700, size: 10, key: "pl" }),
      Row([
        <div key="p1" style={{ position: "relative", width: 44, height: 44, borderRadius: 6, background: grad(mk, "gradBrandStart", "gradBrandEnd") }}><span style={{ position: "absolute", top: -3, right: -3, width: 12, height: 12, borderRadius: "50%", background: mk.c("error"), color: "#fff", fontSize: 8, textAlign: "center", lineHeight: "12px", ...mk.glow("error") }}>×</span></div>,
        PhotoTile(mk, {}),
      ], { key: "ph" }),
      Row([Btn(mk, "Camera", { a: "gradBrandStart", b: "gradBrandEnd" }), Btn(mk, "Gallery", { a: "gradBrandStart", b: "gradBrandEnd" })], { key: "cg" }),
      <span key="sp" style={{ flex: 1 }} />,
      <div key="sub" style={{ background: mk.c("accentPrimary"), borderRadius: 8, padding: "10px 0", textAlign: "center", color: mk.c("textOnPrimary"), fontWeight: 700, fontSize: 12, ...mk.glow("accentPrimary") }}>File Follow-Up Task</div>,
    ]),
  ]);

// Unapproved Jobs: sort toggle + cards (green total, blue underlined address,
// status chip, Invoice/Job Form pill actions).
const unapprovedJobs = (mk) =>
  Screen(mk, [
    AppBar(mk, "Unapproved Jobs", { key: "ab" }),
    Body(mk, [
      Row([Text(mk, "3 jobs", { tok: "textMuted", size: 9 }), spacer("s"), segToggle(mk, ["New-Old", "Old-New"], 0, "srt")], { key: "hd" }),
      ...[["Smith", "$450", "statusPending", "Pending"], ["Nguyen", "$610", "statusInProgress", "In Progress"], ["Patel", "$300", "statusApproved", "Approved"]].map((r, i) =>
        Card(mk, <>
          {Row([Text(mk, r[0], { weight: 700, size: 11 }), spacer("s"), Text(mk, r[1], { tok: "success", weight: 700, size: 11 })])}
          <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}><span style={{ fontSize: 8 }}>📍</span><span style={{ color: mk.c("textLink"), fontSize: 8.5, textDecoration: "underline", ...mk.glow("textLink") }}>1423 Oak St</span></div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}><span style={{ color: mk.c("textMuted"), fontSize: 8 }}>Jul 24 · 9:00a</span>{spacer("s")}{Chip(mk, r[2], r[3])}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 5 }}>
            {["Invoice", "Job Form"].map((a) => <span key={a} style={{ flex: 1, textAlign: "center", fontSize: 8.5, padding: "3px 0", borderRadius: 6, border: `1px solid ${mk.c("accentPrimary")}66`, background: `${mk.c("accentPrimary")}26`, color: mk.c("accentPrimary"), ...mk.glow("accentPrimary") }}>{a}</span>)}
          </div>
        </>, { key: i, gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] })
      ),
    ], { gap: 8 }),
  ]);

// Job Form Review Queue ("Approvals"): cards with #invoice, tech color-dot chips,
// "Submitted: date", "Review & approve ›".
const reviewQueue = (mk) =>
  Screen(mk, [
    AppBar(mk, "Approvals", { key: "ab" }),
    Body(mk, [
      ...[["Smith", "#1042", [["Alex M", "calPeacock"], ["Sam T", "calGrape"]]], ["Nguyen", "#1043", [["Jordan K", "calTangerine"]]]].map((r, i) =>
        Card(mk, <>
          {Row([Text(mk, r[0], { weight: 700, size: 11 }), spacer("s"), Text(mk, r[1], { tok: "textMuted", size: 9 })])}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
            {r[2].map(([n, c], k) => <span key={k} style={{ display: "flex", alignItems: "center", gap: 3 }}>{dot(mk, c, 7)}<span style={{ color: mk.c("textPrimary"), fontSize: 8.5 }}>{n}</span></span>)}
          </div>
          <div style={{ color: mk.c("textMuted"), fontSize: 8, marginTop: 3 }}>Submitted: Jul 24, 2026</div>
          <div style={{ textAlign: "right", marginTop: 3 }}><span style={{ color: mk.c("accentPrimary"), fontSize: 9, fontWeight: 600, ...mk.glow("accentPrimary") }}>Review &amp; approve ›</span></div>
        </>, { key: i, gradKey: ["gradBrandRevStart", "gradBrandRevEnd"] })
      ),
    ], { gap: 8 }),
  ]);

// Classification Review ("Classify Doors"): big door photo + After/Before toggle
// + dropdown grid + switches + Confirm/Skip + batch counter.
const classifyDoors = (mk) =>
  Screen(mk, [
    AppBar(mk, "Classify Doors", { key: "ab", actions: [1] }),
    Body(mk, [
      Text(mk, "Queue 8 · Confirmed 24 · Session 3", { tok: "textMuted", size: 8.5, key: "st" }),
      <div key="ph" style={{ height: 90, borderRadius: 8, background: grad(mk, "gradBrandStart", "gradBrandEnd"), ...mk.glow("gradBrandStart", "gradBrandEnd") }} />,
      Row([spacer("l"), segToggle(mk, ["After", "Before"], 0, "ab2"), spacer("r")], { key: "tg" }),
      Card(mk, <>
        {Input(mk, "Door style (AI 87% · 2nd: Craftsman)", { value: "Shaker", focused: true })}
        <div style={{ height: 7 }} />
        <div style={{ display: "flex", gap: 6 }}><div style={{ flex: 1 }}>{Input(mk, "Material", { value: "Wood" })}</div><div style={{ flex: 1 }}>{Input(mk, "Glass", { value: "None" })}</div></div>
        <div style={{ height: 7 }} />
        {Toggle(mk, "Stained transom", false)}
      </>, { key: "form" }),
      Row([Btn(mk, "Confirm", { a: "gradGreenBlueStart", b: "gradGreenBlueEnd", key: "cf" }), Btn(mk, "Skip", { a: "gradDisabledStart", b: "gradDisabledEnd", key: "sk" })], { key: "btn" }),
      <div key="ct" style={{ textAlign: "center", color: mk.c("textMuted"), fontSize: 8 }}>3 / 12 in this batch</div>,
    ]),
  ]);

// Impersonate User: plain Material look (the one non-gradient page) — search + rows.
const impersonate = (mk) =>
  Screen(mk, [
    AppBar(mk, "Impersonate User", { key: "ab" }),
    Body(mk, [
      <div key="s" style={{ border: `1px solid ${mk.c("inputBorder")}`, borderRadius: 6, padding: "6px 8px", color: mk.c("hintText"), fontSize: 10 }}>🔍 Search by name, email, or role</div>,
      ...[["Alex M", "refinisher"], ["Jordan K", "office"], ["Sam T", "admin · you"]].map((u, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", borderBottom: `1px solid ${mk.c("divider")}`, opacity: i === 2 ? 0.5 : 1 }}>
          <span style={{ width: 20, height: 20, borderRadius: "50%", border: `1.5px solid ${mk.c("textMuted")}`, display: "flex", alignItems: "center", justifyContent: "center", color: mk.c("textMuted"), fontSize: 10 }}>◍</span>
          <div><div style={{ color: mk.c("textPrimary"), fontSize: 11 }}>{u[0]}</div><div style={{ color: mk.c("textMuted"), fontSize: 8.5 }}>{u[1]}</div></div>
        </div>
      )),
    ], { gap: 4 }),
  ]);

// ── Office comms Phase 1 (2026-09-03) ───────────────────────────────────────
// Comms hub (office Home tab): Today|Recents|Search segment, All|Texts|Emails
// filter chips, merged recents rows. Text rows consume `unreadIndicator` for
// the unread dot + strong title (web .text-title-strong parity).
const officeCommsHome = (mk, title, nav, withDrawer) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      Row([spacer("l"), segToggle(mk, ["Today", "Recents", "Search"], 1, "seg"), spacer("r")], { key: "seg" }),
      Row([segToggle(mk, ["All", "Texts", "Emails"], 0, "flt"), spacer("s"), Text(mk, "20 conversations", { tok: "textMuted", size: 8 })], { key: "flt" }),
      ...[["Jane Smith", "512-555-0114", true], ["Bob Nguyen", "512-555-0187", false]].map(([n, num, unread], i) => (
        <div key={`t${i}`} style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 8px", background: mk.c("surfaceListItem"), borderRadius: 8 }}>
          {unread ? dot(mk, "unreadIndicator", 8) : <span style={{ width: 8, flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: mk.c(unread ? "unreadIndicator" : "textPrimary"), fontSize: 10, fontWeight: unread ? 700 : 400, ...(unread ? mk.glow("unreadIndicator") : null) }}>{n}</div>
            <div style={{ color: mk.c("textMuted"), fontSize: 8.5 }}>{num}</div>
          </div>
          <span style={{ color: mk.c("success"), fontSize: 8, ...mk.glow("success") }}>9:41a</span>
        </div>
      )),
      ...[["↓", "Re: Door quote", "jane@x.com"], ["↑", "Invoice 1042", "bob@z.com"]].map(([g, subj, addr], i) => (
        <div key={`e${i}`} style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 8px", background: mk.c("surfaceListItem"), borderRadius: 8 }}>
          <span style={{ color: mk.c("accentPrimary"), fontSize: 10, ...mk.glow("accentPrimary") }}>{g}✉</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: mk.c("textPrimary"), fontSize: 10 }}>{subj}</div>
            <div style={{ color: mk.c("textMuted"), fontSize: 8.5 }}>{addr}</div>
          </div>
          <span style={{ color: mk.c("success"), fontSize: 8 }}>8:15a</span>
        </div>
      )),
    ], { gap: 6 }),
  ], { nav: nav ? BottomNav(mk, nav, 0) : null, overlay: withDrawer ? navDrawer(mk) : null });

// Text thread overlay: message bubbles on the barrier — consumes the new
// `bubbleIn` / `bubbleOut` mints (web msg-page bubble parity) + delivery
// status line + failed bubble state + composer.
const officeTextThread = (mk) => (
  <div style={{ height: "100%", background: mk.c("barrierScrim"), display: "flex", alignItems: "center", justifyContent: "center", padding: 12, ...mk.glow("barrierScrim") }}>
    <div style={{ width: "100%", height: "94%", display: "flex", flexDirection: "column", background: mk.c("surfaceModal"), borderRadius: 12, padding: 10, ...mk.glow("surfaceModal") }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: mk.c("textPrimary"), fontSize: 11, fontWeight: 700, ...mk.glow("textPrimary") }}>Jane Smith</div>
          <div style={{ color: mk.c("textMuted"), fontSize: 8 }}>512-555-0114</div>
        </div>
        <span style={{ color: mk.c("textPrimary"), fontSize: 11 }}>✕</span>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5, overflow: "hidden" }}>
        <div style={{ alignSelf: "flex-start", maxWidth: "80%", background: mk.c("bubbleIn"), borderRadius: 10, padding: "5px 9px", color: mk.c("textPrimary"), fontSize: 9.5, ...mk.glow("bubbleIn") }}>Hi — what time tomorrow?</div>
        <div style={{ alignSelf: "flex-end", maxWidth: "80%", background: mk.c("bubbleOut"), borderRadius: 10, padding: "5px 9px", color: mk.c("textPrimary"), fontSize: 9.5, ...mk.glow("bubbleOut") }}>We'll be there at 8am!</div>
        <div style={{ alignSelf: "flex-end", color: mk.c("textMuted"), fontSize: 7, fontStyle: "italic" }}>delivered · 9:41a</div>
        <div style={{ alignSelf: "flex-end", maxWidth: "80%", background: mk.c("bubbleOut"), borderRadius: 10, padding: "5px 9px", color: mk.c("textPrimary"), fontSize: 9.5, opacity: 0.75 }}>See you then.</div>
        <div style={{ alignSelf: "flex-end", color: mk.c("error"), fontSize: 7.5, ...mk.glow("error") }}>failed — tap to retry</div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
        <div style={{ flex: 1, border: `1px solid ${mk.c("inputBorder")}`, borderRadius: 14, padding: "5px 9px", color: mk.c("hintText"), fontSize: 9 }}>Text message…</div>
        <span style={{ width: 24, height: 24, borderRadius: "50%", background: mk.c("accentPrimary"), color: mk.c("textOnPrimary"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, ...mk.glow("accentPrimary") }}>➤</span>
      </div>
    </div>
  </div>
);

// Compose email overlay: To/Subject/Body + attach + the ALWAYS-on send-confirm
// affordance (email sends are LIVE — plan §Safety asymmetry).
const officeEmailCompose = (mk) => (
  <div style={{ height: "100%", background: mk.c("barrierScrim"), display: "flex", alignItems: "center", justifyContent: "center", padding: 12, ...mk.glow("barrierScrim") }}>
    <div style={{ width: "100%", background: mk.c("surfaceModal"), borderRadius: 12, padding: 12, ...mk.glow("surfaceModal") }}>
      <div style={{ color: mk.c("textPrimary"), fontWeight: 600, fontSize: 12, marginBottom: 8, ...mk.glow("textPrimary") }}>New Email</div>
      {Input(mk, "To", { value: "jane@x.com", focused: true })}
      <div style={{ height: 7 }} />
      {Input(mk, "Subject", { value: "Re: Door quote" })}
      <div style={{ height: 7 }} />
      {Input(mk, "Message", { value: "Hi Jane — attached is the updated…" })}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9 }}>
        <span style={{ color: mk.c("accentPrimary"), fontSize: 9, ...mk.glow("accentPrimary") }}>📎 Attach photo</span>
        <span style={{ flex: 1 }} />
        <span style={{ color: mk.c("textMuted"), fontSize: 10 }}>Cancel</span>
        <span style={{ color: mk.c("accentPrimary"), fontSize: 10, fontWeight: 600, ...mk.glow("accentPrimary") }}>Send…</span>
      </div>
      <div style={{ marginTop: 6, color: mk.c("warning"), fontSize: 7.5, ...mk.glow("warning") }}>Send always confirms — email is live in every environment.</div>
    </div>
  </div>
);

const generic = (mk, title) =>
  Screen(mk, [
    AppBar(mk, title, { key: "ab" }),
    Body(mk, [
      Card(mk, Text(mk, title, { size: 11 }), { key: "c" }),
      ...[0, 1, 2].map((i) => ListRow(mk, `Item ${i + 1}`, "detail", i === 0 ? ["statusApproved", "OK"] : null, { key: i })),
    ], { gap: 6 }),
  ]);

// ── registry ─────────────────────────────────────────────────────────────────────
const R = {
  "shared.login": login,
  "shared.reset-password": (mk) => formScreen(mk, "Reset Password", [{ label: "New Password", value: "••••••" }, { label: "Confirm Password", value: "••••••" }], { btn: "Reset", btnKey: ["gradBrandStart", "gradBrandEnd"] }),
  "shared.change-password": (mk) => formScreen(mk, "Change Password", [{ label: "Current" }, { label: "New Password" }, { label: "Confirm" }], { btn: "Update", btnKey: ["gradBrandStart", "gradBrandEnd"] }),
  "shared.settings": settings,
  "shared.calendar-standalone": (mk) => monthCalendar(mk, "Calendar"),
  "shared.landscape-calendar-view": (mk) => timeline(mk, "Calendar"),
  "shared.client-referral": clientReferral,
  "shared.paycheck-calculator": paycheckCalc,
  "shared.process-payment": payment,
  "shared.schedule-time-off-modal": (mk) => modal(mk, "Schedule Time Off", <>{Input(mk, "Start Date", { value: "May 22" })}<div style={{ height: 9 }} />{Input(mk, "End Date", { value: "May 24" })}</>, { surfaceTok: "surfaceScheduleModal", bordered: true }),
  "shared.inventory-request": inventoryRequest,

  "refinisher.dashboard-home": (mk) => refinisherHome(mk, "Dashboard"),
  "refinisher.dashboard-shell": (mk) => refinisherHome(mk, "BDR", ["Payment", "Inventory", "Calendar", "Dashboard"], true),
  "refinisher.post-job-form": jobForm,
  "refinisher.my-paycheck": (mk) => myPaycheck(mk, "My Paycheck"),
  "refinisher.go-by-followup": goByFollowup,

  "admin.personal-event-modal": personalEventModal,
  "admin.owner-dashboard": (mk) => ownerDashboard(mk, "Dashboard"),
  "admin.dashboard-shell": (mk) => ownerDashboard(mk, "BDR Admin", ["Payroll", "Inventory", "Inbox", "Calendar", "Dashboard"], true),
  "admin.business-ledger": businessLedger,
  "admin.business-metrics": businessMetrics,
  "admin.reports": (mk) => reports(mk, "Reports"),
  "admin.reports.payroll": (mk) => payrollReport(mk, "Payroll Report"),
  "admin.reports.inventory": inventoryReport,
  "admin.reports.shopping": shoppingList,
  "admin.inbox": inbox,
  "admin.sent-emails": sentEmails,
  "admin.unapproved-jobs": unapprovedJobs,
  "admin.job-form-review-queue": reviewQueue,
  "admin.shop-inventory": (mk) => Screen(mk, [
    AppBar(mk, "Shop Inventory", { key: "ab", actions: [1] }),
    Body(mk, [
      ...[["Foam Wedges", "40", "accentPrimary"], ["Weather Strip", "12", "calBanana"], ["Hinges", "60", "accentPrimary"]].map((r, i) =>
        Card(mk, Row([
          <div key="i" style={{ width: 26, height: 26, borderRadius: 6, background: mk.c("surfaceListItem") }} />,
          <div key="n" style={{ flex: 1 }}><div style={{ color: mk.c("textPrimary"), fontSize: 11, fontWeight: 600 }}>{r[0]}</div><div style={{ color: mk.c(r[2]), fontSize: 9, ...mk.glow(r[2]) }}>Stock {r[1]}</div></div>,
          <span key="qr" style={{ width: 20, height: 20, borderRadius: 4, border: `1.5px solid ${mk.c("textPrimary")}`, ...mk.glow("textPrimary") }} />,
        ]), { key: i, flat: true })
      ),
    ], { gap: 8 }),
    Fab(mk, { key: "fab" }),
  ]),
  "admin.inventory-requests": (mk) => listScreen(mk, "Inventory Requests", [{ t: "Alex M", s: "4 items requested", chip: ["statusPending", "New"] }, { t: "Jordan K", s: "2 items requested" }]),
  "admin.user-management": userMgmt,
  "admin.classification-review": classifyDoors,
  "admin.impersonate-user": impersonate,
  "admin.navigation-notes": (mk) => formScreen(mk, "Navigation Notes", [{ label: "Drawer note text", value: "Reminder: submit forms by 5pm" }], { btn: "Save", btnKey: ["gradBrandStart", "gradBrandEnd"] }),
  "admin.promote-build": (mk) => Screen(mk, [
    AppBar(mk, "Promote Build", { key: "ab" }),
    Body(mk, [
      Card(mk, <>{SectionLabel(mk, "Build 272")}{Text(mk, "iOS on TestFlight · Android held", { tok: "textMuted", size: 10 })}</>, { key: "info", flat: true }),
      Card(mk, <>{Toggle(mk, "Schedule for later", false)}</>, { key: "sch" }),
      Btn(mk, "Promote to Live", { a: "gradBrandStart", b: "gradBrandEnd", key: "b" }),
    ]),
  ]),
  "admin.trigger-build": (mk) => formScreen(mk, "Trigger Build", [{ label: "Release notes", value: "Theming + fixes" }], { btn: "Trigger iOS Build", btnKey: ["gradBrandStart", "gradBrandEnd"] }),
  "admin.server-health": (mk) => consoleScreen(mk, "Server Health"),
  "admin.dev-console": (mk) => consoleScreen(mk, "Dev Console"),

  "office.dashboard-shell": (mk) => officeCommsHome(mk, "Comms", ["Comms", "Inbox", "Calendar"], true),
  "office.comms-home": (mk) => officeCommsHome(mk, "Comms"),
  "office.text-thread": officeTextThread,
  "office.email-compose": officeEmailCompose,
  "admin.fcm-debug-console": (mk) => listScreen(mk, "FCM Debug", [{ t: "Token", s: "registered", chip: ["success", "OK"] }, { t: "Device", s: "iPhone 15 Pro" }, { t: "Active tokens", s: "3" }, { t: "Pending", s: "0" }]),
};

export function renderMock(pageId, mk) {
  if (!pageId) return ownerDashboard(mk, "BDR");
  const fn = R[pageId];
  if (fn) return fn(mk);
  return generic(mk, pageId.split(".").slice(1).join(" · "));
}
